import { Fragment, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDepartments, useEmploymentTypes, usePositions } from '@/hooks/use-organizations';
import { useEmployees } from '@/hooks/use-employees';

/** Sentinel for "no value" — Radix Select rejects an empty string as an item value. */
const NONE = '__none__';

export interface EmployeeDraft {
  firstName?: string;
  lastName?: string;
  workEmail?: string;
  startDate?: string;
  positionId?: string;
  departmentId?: string;
  managerId?: string;
  employmentTypeId?: string;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[6.5rem_1fr] items-center gap-x-3">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

/**
 * The create-employee proposal, as fields rather than a printout.
 *
 * The assistant is good at reading a sentence and bad at knowing that this
 * company calls the job "Node.js Dev" and not "JS Engineer". Asking it to guess
 * again in chat is the slow path; picking from the real list is the fast one. So
 * everything it filled in is editable here, against the same lookups the manual
 * employee form uses — the model proposes, the human corrects, and what gets
 * applied is whatever is on screen at the moment of approval.
 */
export function CreateEmployeeFields({
  draft,
  onChange,
  disabled,
  readOnly,
}: {
  draft: EmployeeDraft;
  onChange: (patch: EmployeeDraft) => void;
  disabled?: boolean;
  /** After the record exists: show what was written, from the same lookups. */
  readOnly?: boolean;
}) {
  const { data: positions = [] } = usePositions();
  const { data: departments = [] } = useDepartments();
  const { data: employmentTypes = [] } = useEmploymentTypes();
  const { data: employeesPage } = useEmployees({ limit: 100, status: 'active' });

  const managers = useMemo(() => employeesPage?.data ?? [], [employeesPage]);

  // The applied card is rendered from the same draft the form edited, so it can
  // never show a value other than the one that was submitted. Reading back the
  // assistant's original proposal here was a small lie with a large effect:
  // someone who picked a position saw "—" afterwards and concluded their choice
  // had been dropped, when it had in fact been written.
  if (readOnly) {
    const rows: [string, string][] = [
      ['Name', [draft.firstName, draft.lastName].filter(Boolean).join(' ') || '—'],
      ['Work email', draft.workEmail || '—'],
      ['Start date', draft.startDate || '—'],
      ['Position', positions.find((p) => p.id === draft.positionId)?.title ?? '—'],
      ['Department', departments.find((d) => d.id === draft.departmentId)?.name ?? '—'],
      [
        'Manager',
        (() => {
          const m = managers.find((x) => x.id === draft.managerId);
          return m ? `${m.firstName} ${m.lastName}` : '—';
        })(),
      ],
      ['Employment', employmentTypes.find((t) => t.id === draft.employmentTypeId)?.name ?? '—'],
    ];

    return (
      <dl className="mt-2 grid grid-cols-[6.5rem_1fr] gap-x-3 gap-y-1">
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <dt className="text-[11px] text-muted-foreground">{label}</dt>
            <dd className="min-w-0 break-words text-[11px] font-medium text-foreground">{value}</dd>
          </Fragment>
        ))}
      </dl>
    );
  }

  const field = (key: keyof EmployeeDraft) => ({
    value: draft[key] ?? '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange({ [key]: e.target.value }),
    disabled,
    className: 'h-7 text-xs',
  });

  const picker = (key: keyof EmployeeDraft) => ({
    value: draft[key] ?? NONE,
    onValueChange: (v: string) => onChange({ [key]: v === NONE ? undefined : v }),
    disabled,
  });

  return (
    <div className="mt-2 space-y-1.5">
      <Row label="First name">
        <Input {...field('firstName')} aria-label="First name" />
      </Row>
      <Row label="Last name">
        <Input {...field('lastName')} aria-label="Last name" />
      </Row>
      <Row label="Work email">
        <Input {...field('workEmail')} type="email" aria-label="Work email" />
      </Row>
      <Row label="Start date">
        <Input {...field('startDate')} type="date" aria-label="Start date" />
      </Row>

      <Row label="Position">
        <Select {...picker('positionId')}>
          <SelectTrigger className="h-7 text-xs" aria-label="Position">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Not set</SelectItem>
            {positions.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>

      <Row label="Department">
        <Select {...picker('departmentId')}>
          <SelectTrigger className="h-7 text-xs" aria-label="Department">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Not set</SelectItem>
            {departments.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>

      <Row label="Manager">
        <Select {...picker('managerId')}>
          <SelectTrigger className="h-7 text-xs" aria-label="Manager">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Not set</SelectItem>
            {managers.map((m) => (
              <SelectItem key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>

      <Row label="Employment">
        <Select {...picker('employmentTypeId')}>
          <SelectTrigger className="h-7 text-xs" aria-label="Employment type">
            <SelectValue placeholder="Not set" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Not set</SelectItem>
            {employmentTypes.map((t) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Row>
    </div>
  );
}
